<template>
  <v-container class="py-12">
    <TitleHeader>Legislação</TitleHeader>

    <!-- Breadcrumb -->
    <nav class="breadcrumb mb-6">
      <NuxtLink
        to="/"
        class="link-anchor"
      >
        <v-icon
          small
          class="mr-1"
          >mdi-home</v-icon
        >
        Início
      </NuxtLink>
      <v-icon
        size="small"
        class="mx-2 text-medium-emphasis"
        >mdi-chevron-right</v-icon
      >
      <NuxtLink
        to="/funarpen/publicacoes"
        class="link-anchor"
      >
        <v-icon
          small
          class="mr-1"
          >mdi-book-open-variant</v-icon
        >
        Publicações
      </NuxtLink>
      <v-icon
        size="small"
        class="mx-2 text-medium-emphasis"
        >mdi-chevron-right</v-icon
      >
      <span class="current">
        <v-icon
          small
          class="mr-1"
          >mdi-scale-balance</v-icon
        >
        Legislação
      </span>
    </nav>

    <v-row
      class="w-100 max-width mx-auto"
      align="start"
      justify="center"
      no-gutters
    >
      <v-col
        cols="12"
        sm="12"
        md="11"
        lg="10"
        xl="9"
      >
        <v-card
          elevation="10"
          class="pa-6 rounded-xl bg-form-card border-left-institucional"
        >
          <template v-if="loading">
            <div
              class="loading-state"
              aria-live="polite"
              role="status"
            >
              <div
                class="spinner"
                aria-hidden="true"
              ></div>
              <p class="loading-text">Carregando informações...</p>
            </div>
          </template>

          <template v-else-if="erro">
            <p class="status-message error">{{ erro }}</p>
          </template>

          <template v-else-if="resultado && resultado.length">
            <v-list dense>
              <v-list-item
                v-for="(lei, index) in resultado"
                :key="index"
                class="arquivo-item"
              >
                <v-list-item>
                  <v-list-item-title class="arquivo-titulo">
                    <NuxtLink
                      :to="getLeiLink(lei)"
                      class="arquivo-link"
                    >
                      {{ lei.titulo }}
                    </NuxtLink>
                  </v-list-item-title>
                </v-list-item>
              </v-list-item>
            </v-list>
          </template>

          <template v-else>
            <div class="empty-state">
              <div
                class="empty-icon"
                aria-hidden="true"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6m7 1.5L18.5 9H13V3.5M8 13h8v2H8v-2m0 4h8v2H8v-2m0-8h5v2H8V9Z"
                  />
                </svg>
              </div>
              <h2 class="empty-title">Nenhuma legislação encontrada</h2>
              <p class="empty-description">Ainda não há registros disponíveis nesta seção<br /></p>
            </div>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRuntimeConfig } from '#imports'
import { useRouter } from 'vue-router'
import TitleHeader from '@/components/TitleHeader.vue'

definePageMeta({
  middleware: 'manutencao',
  title: 'FUNARPEN - Publicações - Legislação',
})

useHead({
  title: 'Legislação',
  meta: [{ name: 'description', content: 'Legislação' }],
})

const router = useRouter()
const config = useRuntimeConfig()
const baseUrl = config.public.backofficeApiBaseUrl
const resultado = ref([])
const loading = ref(true)
const erro = ref('')

function getLeiLink(lei) {
  return {
    path: '/funarpen/publicacoes/leis/lei',
    query: {
      title: encodeURIComponent(lei.titulo),
      description: encodeURIComponent(lei.descricao),
    },
  }
}

async function buscarLeis() {
  loading.value = true
  erro.value = ''

  try {
    const subsecao = encodeURIComponent('Legislação')
    const res = await fetch(`${baseUrl}/portal/paginas?portal=Portal FUNARPEN&subsecao=${subsecao}`)

    if (res.status === 401) {
      router.push('/manutencao/acesso-negado')
    } else if (res.status === 404) {
      resultado.value = []
      return
    }

    if (!res.ok) throw new Error('Erro ao buscar dados')

    const data = await res.json()
    resultado.value = Array.isArray(data) ? data : []
  } catch (e) {
    erro.value = e.message.includes('Failed to fetch') ? 'Erro de conexão. Tente novamente mais tarde.' : e.message
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  buscarLeis()
})
</script>

<style scoped>
/* ==================
   LAYOUT GERAL
===================== */
.max-width {
  max-width: 1200px;
  box-sizing: border-box;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.bg-form-card {
  background-color: rgba(255, 255, 255, 0.92);
}

.border-left-institucional {
  border-left: 6px solid var(--funarpen-blue);
}

/* ========================
   LISTAGEM DE ARQUIVOS
=========================== */
.arquivo-item {
  padding: 0.8rem 0;
  border-bottom: 1px dashed rgba(8, 47, 73, 0.14);
}

.arquivo-titulo {
  white-space: normal;
  word-break: break-word;
}

.arquivo-link {
  color: var(--funarpen-blue);
  text-decoration: none;
  font-weight: 600;
}

.arquivo-link:hover {
  text-decoration: underline;
}

/* =====================
   ESTADOS DE STATUS
======================== */
.status-message {
  font-size: 1.2rem;
  text-align: center;
  color: var(--funarpen-red);
  font-weight: 600;
}

/* ======================
   LOADING / SPINNER
========================= */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: rgba(11, 69, 113, 0.8);
}

.spinner {
  width: 64px;
  height: 64px;
  border: 6px solid rgba(11, 69, 113, 0.2);
  border-top-color: rgba(11, 69, 113, 0.7);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  font-weight: 500;
}

/* ======================
   ESTADO VAZIO
========================= */
.empty-state {
  text-align: center;
  color: rgba(11, 69, 113, 0.85);
}

.empty-icon svg {
  width: 72px;
  height: 72px;
  fill: rgba(11, 69, 113, 0.6);
}

.empty-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.empty-description {
  font-size: 1.125rem;
  font-weight: 400;
  color: rgba(11, 69, 113, 0.75);
  line-height: 1.6;
}

/* =====================
   BREADCRUMB
======================== */
.breadcrumb {
  font-size: 0.9rem;
  display: flex;
  align-items: center;

  /* permite quebra de linha */
  flex-wrap: wrap;
  justify-content: flex-start;

  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.breadcrumb .link-anchor {
  text-decoration: none;
  color: var(--funarpen-muted);
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  border-radius: 6px;

  /* cada item não quebra sozinho, mas o conjunto pode ir pra próxima linha */
  white-space: nowrap;
}

.breadcrumb .link-anchor:hover {
  color: var(--funarpen-blue-2);
  background: rgba(11, 79, 108, 0.05);
}

.breadcrumb .sep {
  margin: 0 0.5rem;
  color: var(--funarpen-muted);
  user-select: none;
}

.breadcrumb .current {
  color: var(--funarpen-ink);
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

/* ====================================================
   RESPONSIVIDADE
==================================================== */
@media (max-width: 600px) {
  /* breadcrumb mais compacto */
  .breadcrumb {
    font-size: 0.8rem;
  }

  .breadcrumb .link-anchor {
    padding: 3px 4px;
  }

  .breadcrumb .v-icon {
    font-size: 16px;
    margin: 0 2px;
  }
}

@media (max-width: 400px) {
  /* breadcrumb ultra compacto */
  .breadcrumb {
    font-size: 0.7rem;
  }

  .breadcrumb .v-icon {
    font-size: 14px;
  }
}
</style>
